import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Alert,
  Button,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Login as LoginIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // ユーザーが既にログインしている場合はダッシュボードにリダイレクト
  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  // Cognito Hosted UIへのリダイレクト
  const redirectToCognitoHostedUI = async () => {
    try {
      setIsRedirecting(true);
      setError(null);

      console.log('🔄 Redirecting to Cognito Hosted UI using Amplify signInWithRedirect (auto-detect providers)');
      
      // AmplifyのsignInWithRedirectを使用（プロバイダー指定なし - Cognito側で自動表示）
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect();
      
    } catch (err) {
      console.error('💥 Failed to redirect to Cognito Hosted UI:', err);
      setError('認証システムへの接続に失敗しました。しばらく時間をおいて再度お試しください。');
      setIsRedirecting(false);
    }
  };

  // OAuth認証コードの処理（詳細デバッグ版）
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const fullUrl = window.location.href;
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      console.log('🔍 OAuth Callback Debug:', {
        fullUrl,
        search: window.location.search,
        hash: window.location.hash,
        urlParams: Object.fromEntries(urlParams.entries()),
        hashParams: Object.fromEntries(hashParams.entries())
      });
      
      // URLパラメータとハッシュの両方をチェック
      const authCode = urlParams.get('code') || hashParams.get('code');
      const authError = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
      const state = urlParams.get('state') || hashParams.get('state');
      
      if (authError) {
        console.error('💥 OAuth Error Details:', {
          error: authError,
          description: errorDescription,
          state: state,
          fullUrl: fullUrl
        });
        setError(`認証エラー: ${authError}${errorDescription ? ` - ${errorDescription}` : ''}`);
        // URLをクリーンアップ
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        return;
      }
      
      if (authCode) {
        console.log('🔄 Processing OAuth callback with code:', {
          code: authCode.substring(0, 20) + '...',
          state: state,
          fullUrl: fullUrl
        });
        try {
          // URLをクリーンアップ
          const newUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          
          // 認証状態を再確認（useAuthのcheckAuthStateが自動的に処理）
          // 少し待ってからダッシュボードにリダイレクト
          setTimeout(() => {
            if (user) {
              navigate('/dashboard');
            }
          }, 1000);
        } catch (err) {
          console.error('💥 OAuth callback processing failed:', err);
          setError('認証処理に失敗しました。再度ログインしてください。');
        }
      } else if (window.location.pathname === '/auth/callback') {
        // /auth/callbackにいるのにコードがない場合の詳細ログ
        console.error('⚠️ OAuth Callback Missing Code:', {
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
          referrer: document.referrer,
          timestamp: new Date().toISOString()
        });
        setError('認証コードが取得できませんでした。認証プロセスに問題がある可能性があります。');
      }
    };
    
    handleOAuthCallback();
  }, [user, navigate]);

  // デバッグ情報の状態管理
  const [debugInfo, setDebugInfo] = useState<any>(null);

  // URLパラメータとデバッグ情報を取得（詳細版）
  useEffect(() => {
    const fetchDetailedDebugInfo = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const currentUrl = window.location.href;
      const origin = window.location.origin;
      const pathname = window.location.pathname;
      
      // Amplifyセッション情報を詳細取得
      let amplifySessionInfo = null;
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        
        amplifySessionInfo = {
          isSignedIn: !!session.tokens,
          tokens: {
            accessToken: session.tokens?.accessToken ? 'exists' : 'missing',
            idToken: session.tokens?.idToken ? 'exists' : 'missing',
            refreshToken: (session.tokens as any)?.refreshToken ? 'exists' : 'missing'
          },
          credentials: session.credentials ? 'exists' : 'missing',
          identityId: session.identityId || 'missing'
        };
        
        // IDトークンの詳細解析
        if (session.tokens?.idToken) {
          try {
            const idTokenString = session.tokens.idToken.toString();
            const tokenParts = idTokenString.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              amplifySessionInfo.idTokenPayload = {
                sub: payload.sub,
                email: payload.email,
                name: payload.name,
                'cognito:groups': payload['cognito:groups'],
                exp: new Date(payload.exp * 1000).toISOString(),
                iat: new Date(payload.iat * 1000).toISOString(),
                token_use: payload.token_use,
                aud: payload.aud,
                iss: payload.iss
              };
            }
          } catch (tokenError) {
            amplifySessionInfo.tokenParseError = String(tokenError);
          }
        }
      } catch (sessionError) {
        amplifySessionInfo = {
          error: String(sessionError),
          errorName: (sessionError as any)?.name
        };
      }
      
      // authServiceのデバッグ情報も取得
      let authServiceInfo = null;
      try {
        const { authService } = await import('../services/authService');
        authServiceInfo = await authService.getDebugInfo();
      } catch (authServiceError) {
        authServiceInfo = {
          error: String(authServiceError)
        };
      }
      
      const debugData = {
        currentUrl,
        origin,
        pathname,
        urlParams: Object.fromEntries(urlParams.entries()),
        userState: user ? {
          userExists: true,
          userId: (user as any).userId || 'unknown',
          username: (user as any).username || 'unknown', 
          attributes: (user as any).attributes || {},
          signInDetails: (user as any).signInDetails || 'missing'
        } : 'not authenticated',
        amplifySession: amplifySessionInfo,
        authService: authServiceInfo,
        amplifyConfig: {
          userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
          clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
          domain: import.meta.env.VITE_COGNITO_DOMAIN,
          redirectSignIn: import.meta.env.VITE_REDIRECT_SIGN_IN,
          redirectSignOut: import.meta.env.VITE_REDIRECT_SIGN_OUT
        },
        environmentInfo: {
          nodeEnv: import.meta.env.MODE,
          baseUrl: import.meta.env.BASE_URL,
          dev: import.meta.env.DEV
        },
        localStorage: {
          cognitoKeys: Object.keys(localStorage).filter(key => 
            key.includes('cognito') || key.includes('amplify') || key.includes('CognitoIdentityServiceProvider')
          )
        }
      };
      
      setDebugInfo(debugData);
      console.log('🔍 Detailed Login Debug Info:', debugData);
    };
    
    fetchDetailedDebugInfo();
  }, [user]);

  // 自動リダイレクトは無効化
  // useEffect(() => {
  //   if (!user && !loading && !isRedirecting) {
  //     const urlParams = new URLSearchParams(window.location.search);
  //     const hasAuthCode = urlParams.has('code');
  //     const hasAuthError = urlParams.has('error');
  //     
  //     if (!hasAuthCode && !hasAuthError) {
  //       const timer = setTimeout(() => {
  //         redirectToCognitoHostedUI();
  //       }, 1500);
  //       
  //       return () => clearTimeout(timer);
  //     }
  //   }
  // }, [user, loading, isRedirecting]);

  // ローディング中の表示
  if (loading || isRedirecting) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          py={4}
        >
          <Paper
            elevation={3}
            sx={{
              width: '100%',
              maxWidth: 400,
              p: 4,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <SecurityIcon 
              sx={{ 
                fontSize: 64, 
                color: 'primary.main', 
                mb: 2 
              }} 
            />
            
            <Typography variant="h5" component="h1" gutterBottom>
              Group Content Portal
            </Typography>
            
            <Typography variant="body1" color="text.secondary" paragraph>
              {isRedirecting 
                ? 'セキュアな認証システムに接続中...' 
                : '認証状態を確認中...'}
            </Typography>
            
            <CircularProgress size={40} sx={{ mt: 2 }} />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {isRedirecting 
                ? 'Cognito認証画面に自動的にリダイレクトします' 
                : 'しばらくお待ちください'}
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  // エラー表示またはマニュアルログイン
  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 400,
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <SecurityIcon 
            sx={{ 
              fontSize: 64, 
              color: 'primary.main', 
              mb: 2 
            }} 
          />
          
          <Typography variant="h5" component="h1" gutterBottom>
            Group Content Portal
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            企業向け統合認証システム
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={redirectToCognitoHostedUI}
            disabled={isRedirecting}
            sx={{
              py: 1.5,
              mb: 2,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
              },
            }}
          >
            {isRedirecting ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                接続中...
              </>
            ) : (
              'セキュアログイン'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            AWS Cognitoによる安全な認証
          </Typography>

          {error && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setError(null);
                redirectToCognitoHostedUI();
              }}
              sx={{ mt: 1 }}
            >
              再試行
            </Button>
          )}

          {/* デバッグ情報表示 */}
          {debugInfo && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '0.9rem', fontWeight: 'bold' }}>
                🔍 認証デバッグ情報
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                <strong>現在URL:</strong> {debugInfo.currentUrl}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                <strong>Origin:</strong> {debugInfo.origin}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                <strong>Path:</strong> {debugInfo.pathname}
              </Typography>
              
              {Object.keys(debugInfo.urlParams).length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                    URLパラメータ:
                  </Typography>
                  {Object.entries(debugInfo.urlParams).map(([key, value]) => (
                    <Typography key={key} variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      {key}: {String(value)}
                    </Typography>
                  ))}
                </Box>
              )}
              
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                <strong>認証状態:</strong> {typeof debugInfo.userState === 'string' ? debugInfo.userState : JSON.stringify(debugInfo.userState, null, 2)}
              </Typography>
              
              {/* Amplifyセッション情報 */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                  Amplifyセッション:
                </Typography>
                {debugInfo.amplifySession?.error ? (
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, color: 'error.main' }}>
                    エラー: {debugInfo.amplifySession.error}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      サインイン状態: {debugInfo.amplifySession?.isSignedIn ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      Access Token: {debugInfo.amplifySession?.tokens?.accessToken}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      ID Token: {debugInfo.amplifySession?.tokens?.idToken}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      Refresh Token: {debugInfo.amplifySession?.tokens?.refreshToken}
                    </Typography>
                    {debugInfo.amplifySession?.idTokenPayload && (
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                          IDトークン内容:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Email: {debugInfo.amplifySession.idTokenPayload.email}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Sub: {debugInfo.amplifySession.idTokenPayload.sub}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Groups: {JSON.stringify(debugInfo.amplifySession.idTokenPayload['cognito:groups'])}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Exp: {debugInfo.amplifySession.idTokenPayload.exp}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* authService情報 */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                  AuthService状態:
                </Typography>
                {debugInfo.authService?.error ? (
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1, color: 'error.main' }}>
                    エラー: {debugInfo.authService.error}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      認証済み: {debugInfo.authService?.isAuthenticated ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      トークンキャッシュ: {debugInfo.authService?.tokenCached ? 'Cached' : 'Not Cached'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                      トークン期限: {debugInfo.authService?.tokenExpiry ? debugInfo.authService.tokenExpiry.toString() : 'Unknown'}
                    </Typography>
                    {debugInfo.authService?.user && (
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                          ユーザー情報:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Email: {debugInfo.authService.user.email}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Role: {debugInfo.authService.user.role}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                          Groups: {JSON.stringify(debugInfo.authService.user.groups)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* ローカルストレージ情報 */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                  ローカルストレージ:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  Cognitoキー数: {debugInfo.localStorage?.cognitoKeys?.length || 0}
                </Typography>
                {debugInfo.localStorage?.cognitoKeys?.length > 0 && (
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', ml: 1 }}>
                    キー: {debugInfo.localStorage.cognitoKeys.join(', ')}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                  Amplify設定:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  User Pool ID: {debugInfo.amplifyConfig.userPoolId}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  Client ID: {debugInfo.amplifyConfig.clientId}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  Domain: {debugInfo.amplifyConfig.domain}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  Redirect Sign In: {debugInfo.amplifyConfig.redirectSignIn}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 1 }}>
                  Redirect Sign Out: {debugInfo.amplifyConfig.redirectSignOut}
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
                <strong>環境:</strong> {debugInfo.environmentInfo.nodeEnv} (DEV: {debugInfo.environmentInfo.dev ? 'Yes' : 'No'})
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Box textAlign="center" mt={4}>
            <Typography variant="body2" color="text.secondary">
              © 2024 Group Content Portal
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
